import { useMutation, useQueryClient } from '@tanstack/react-query';
import { del, get } from '@/shared/api/api-client';

/**
 * Archive (soft-delete) a practitioner.
 *   DELETE /api/Doctor/{doctorSetupId}
 *
 * Mirrors Flutter `deleteDoctorById` (settings_data_source_impl.dart:859 —
 * `client.delete('${PracticeService.Doctor}/$doctorSetupId')`). The row leaves
 * the active PractitionerRoles list and surfaces under the archived list; the
 * caller refetches the doctors-list key on success.
 *
 * Bodiless path DELETE — CONFIRMED via dev cURL 2026-06-02:
 *   DELETE /api/Doctor/50bdb6be-f799-ec11-94f6-0050f2f18d1f
 */
export function useArchiveDoctor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (doctorSetupId: string) =>
      del<unknown>(`/api/Doctor/${doctorSetupId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'doctors-list'] });
    },
  });
}

/**
 * Restore an archived practitioner.
 *   GET /api/Doctor/{doctorSetupId}UnArchivedDoctor   (Flutter parity — GET for a mutation)
 *
 * ⚠️ Path quirk copied VERBATIM from Flutter `unArchiveDoctor`
 * (settings_data_source_impl.dart:603 —
 * `client.get('${PracticeService.Doctor}/${id}UnArchivedDoctor')`). There is
 * NO slash between the id and `UnArchivedDoctor` — unlike the staff variant
 * (`/{id}/UnArchivedStaff`, which DOES have the slash). CONFIRMED real via dev
 * cURL 2026-06-02:
 *   GET /api/Doctor/50bdb6be-f799-ec11-94f6-0050f2f18d1fUnArchivedDoctor
 * The missing slash is genuine backend behaviour, not a Flutter bug — do NOT
 * "fix" it to a slash.
 */
export function useUnarchiveDoctor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (doctorSetupId: string) =>
      // NO slash before "UnArchivedDoctor" — verbatim backend shape, NOT a typo.
      // Confirmed via dev cURL (PR #403); staff's equivalent DOES have a slash.
      get<unknown>(`/api/Doctor/${doctorSetupId}UnArchivedDoctor`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'doctors-list'] });
    },
  });
}
